

import { Logger } from '@/util/logger';
import { Zoomer } from './zoomer';

/**
 *
 * @class EasingZoomer
 *
 * This variation of zooming in/out is based on an easing curve that gets used from
 * fully zoomed out, to all the way zoomed in.  When you spin the mouse wheel you
 * are just moving along pre-calculated points of the z-axis.  Those points, are
 * interpolated from one of the supported easing curves (which includes LINEAR).
 *
 * This can be useful because zooming through a 3D z-axis while its projected on a
 * 2D screen (i.e. your browser!) is not necessarily perceived as a linear operation.
 * This can be compounded by the time scale axis have its own "break points" where it
 * changes its granularity so as not to show tick marks with too much density.
 *
 * Those break points may not be spread out in a linear way either.  Using an easing
 * curve can help compensate ane make the scrolling amounts to zoom from one time scale
 * break point to another appear more linear.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2023
 *
 */
export class EasingZoomer extends Zoomer {

  /** the scroll speed; we do this many "lines" for each wheel tick */
  private speed: number = 15;

  /** divides the z extent to get a "line height", smaller values will make the scrolling finer grained */
  private smooth: number = 300;

  /** are already animating? */
  private moving: boolean = false;

  /** the z camera position we want to eventually get to. */
  private target: number = 0;

  /** the ID for the next animation frame callback */
  private animateID: number = 0;

  /** the most recent wheel event */
  private lastEvent: WheelEvent | null = null;

  constructor(canvas: HTMLCanvasElement,
              getZ: ()    => number,
              getNear: () => number,
              getFar: ()  => number,
              zoomDelta: (delta: number, event: WheelEvent) => void,
              filtered: (event: WheelEvent) => boolean) {

    super(canvas, getZ, getNear, getFar, zoomDelta, filtered);

    let ez: EasingZoomer = this;

    ez.target = ez.getPosition();

    //Logger.debug(`[EasingZoomer] constructor -  ready!`);

  }

  /** react to the mouse wheel */
  public scrolled(event: WheelEvent): void {

    let ez: EasingZoomer = this;

    ez.lastEvent = event;

    if (ez.filtered(event)) {

      /* we don't handle it, for example if they have the SHIFT key pressed or something */

      return;
    }

    /* direction to scroll in? What is the closest easing position? */

    /*
    let direction: number   = (ez.normalizedDelta(event) < 0) ? -1 : 1;
    let lutPos: number      = ez.lut.reset(ez.getPosition());
    let scrollPos: number   = (direction < 0) ? ez.lut.previous(ez.speed) : ez.lut.next(ez.speed);
    let scrollDelta: number = scrollPos - ez.getPosition();
    */

    let direction: number   = ez.normalizedDelta(event);
    let lutPos: number      = ez.lut.reset(ez.getPosition());
    let scrollPos: number   = (direction < 0) ? ez.lut.previous() : ez.lut.next();
    let scrollDelta: number = direction * ez.speed * ez.lut.getTween();
    /* let scrollDelta: number = direction * Math.pow(ez.speed * ez.lut.getTween(), 2); */

    /* update the place we are moving to */

    ez.target = ez.getPosition() + scrollDelta;

    let newLutPos: number = ez.lut.reverseLookUp(ez.target);

    if (newLutPos == lutPos) {

      /* delta fell between steps, just take a step. */

      scrollDelta = scrollPos - ez.getPosition();
      ez.target   = scrollPos;
    }

    /* don't go outside the camera clipping bounds */

    if (ez.target <= 0.5) {

      ez.target = 0.5;
    }

    if (ez.target >= ez.getMaximum()) {

      ez.target = ez.getMaximum() - 0.01;
    }

    //Logger.debug(`[EasingZoomer] scrolled -  range=${ez.lut.range()}, lutPos=${lutPos}, pos=${ez.getPosition()}, target=${ez.target}, sd=${scrollDelta}, sp=${scrollPos}, event=`, event);

    /* kick off animation if we need to */

    ez.animate();

  }

  /** if there is any zoom animation happening already, cancel it. */
  public cancel() {

    let ez: EasingZoomer = this;

    /* stop animating the "scroll" */

    if (ez.animateID) {

      cancelAnimationFrame(ez.animateID);

      ez.animateID = 0;
    }

    ez.target = ez.getPosition();

    ez.moving = false;
  }

  /** fetch the proportional "line height" for the camera z-axis */
  public lineHeight(): number {

    let ez: EasingZoomer = this;

    /*
     * divide the entire range of z (the possible camera movement) into "lines";
     * our delta is then a normalized single line of movement in one direction or
     * the other.
     *
     */

    let range: number  = ez.getMaximum() - ez.getMinimum();

    return range /  ez.smooth;
  }

  public animate(): void {

    let ez: EasingZoomer = this;

    ez.moving = true;

    /* scroll by a line, or as close to the final position as we can */

    let delta: number = ez.target - ez.getPosition();

    if (delta == 0) {

      /* we've arrived. */

      ez.cancel();

      //Logger.debug(`[EasingZoomer] animate - new position reached! (${ez.getPosition()})`);

      return ;
    }

    if (Math.abs(delta) > ez.lineHeight()) {

      if (delta < 0) {

        delta = -ez.lineHeight();

      } else {

        delta = ez.lineHeight();
      }
    }

    //Logger.debug(`[MagicZoomer] animate - pos=${mz.getPosition()}, target=${mz.target}, zoom delta:(${delta})`);

    ez.zoomDelta(delta, ez.lastEvent);

    /* wait a little bit (constant) before doing the next one, so we have a linear animation */

    ez.animateID = requestAnimationFrame(() => {ez.animate()});
  }

  /** calculate a wheel delta, accounting fot the browser variation */
  public normalizedDelta(event: WheelEvent): number {

    let ez: EasingZoomer = this;

    /*
     * this is kind of a fool's errand; browsers can have wildly different
     * models of the delta on a wheel movement, and it may change in the future
     * as well.
     *
     * But...we're looking for a simplistic / fast to implement model here.
     *
     * We can at least try to get the best possible normalization:
     *
     *   https://github.com/basilfx/normalize-wheel/blob/master/src/normalizeWheel.js
     *   https://stackoverflow.com/questions/5527601/normalizing-mousewheel-speed-across-browsers
     *
     * We use the direction only of the wheel, and we make our own delta for each
     * tick of the wheel. We want to move by a "line" at a time. We don't try to
     * use the pixel delta provided by the browser, its too unpredictable and
     * anyway, it may change in the future.
     *
     */

    let direction: number = (event.deltaY < 0) ? -1 : 1;

    //Logger.debug(`[EasingZoomer] normalizedDelta - dir=${direction}, event=`, event);

    return direction
  }
}
